import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export const PRODUCT_IMPORT_COLUMNS = [
  'sku_number',
  'product_name',
  'product_description',
  'volume',
  'alcohol_abv',
  'commodity_code',
  'country_of_manufacture',
  'shelf_life',
  'item_type',
  'quantity_per_case',
  'bottle_material',
  'cases_per_pallet',
  'net_case_weight',
  'gross_case_weight',
  'case_height_mm',
  'case_length_mm',
  'case_width_mm',
  'rrp_per_bottle_gbp',
  'currency_rrp_bottle',
  'rrp_per_case_b2b_gbp',
  'currency_rrp_case',
  'cost_price_per_bottle_gbp',
  'currency_cost_bottle',
  'cost_price_per_case_gbp',
  'currency_cost_case',
];

const REQUIRED_COLUMNS = [
  'sku_number',
  'product_name',
  'commodity_code',
  'country_of_manufacture',
  'volume',
];

const INTEGER_COLUMNS = [
  'quantity_per_case',
  'cases_per_pallet',
  'case_height_mm',
  'case_length_mm',
  'case_width_mm',
];

const NUMBER_COLUMNS = [
  'volume',
  'net_case_weight',
  'gross_case_weight',
  'rrp_per_bottle_gbp',
  'rrp_per_case_b2b_gbp',
  'cost_price_per_bottle_gbp',
  'cost_price_per_case_gbp',
];

const CURRENCY_COLUMNS = [
  'currency_rrp_bottle',
  'currency_rrp_case',
  'currency_cost_bottle',
  'currency_cost_case',
];

const ALLOWED_ITEM_TYPES = new Set(['bottle', 'can', 'item']);
const ALLOWED_CURRENCIES = new Set(['GBP', 'USD', 'EUR', 'CHF', 'AUD', 'CAD']);

const HEADER_LOOKUP = PRODUCT_IMPORT_COLUMNS.reduce((acc, header) => {
  acc[header.toLowerCase()] = header;
  return acc;
}, {});

const EXAMPLE_ROW = {
  sku_number: 'SKU-001',
  product_name: 'Example Product',
  product_description: 'Optional description',
  volume: 700,
  alcohol_abv: '40%',
  commodity_code: '2208',
  country_of_manufacture: 'United Kingdom',
  shelf_life: '24 months',
  item_type: 'bottle',
  quantity_per_case: 6,
  bottle_material: 'Glass',
  cases_per_pallet: 100,
  net_case_weight: 8.5,
  gross_case_weight: 9.2,
  case_height_mm: 300,
  case_length_mm: 250,
  case_width_mm: 200,
  rrp_per_bottle_gbp: 24.99,
  currency_rrp_bottle: 'GBP',
  rrp_per_case_b2b_gbp: 119.99,
  currency_rrp_case: 'GBP',
  cost_price_per_bottle_gbp: 14.5,
  currency_cost_bottle: 'GBP',
  cost_price_per_case_gbp: 87,
  currency_cost_case: 'GBP',
};

const normalizeRawRow = (rawRow) => {
  const normalized = {};
  Object.keys(rawRow || {}).forEach((rawKey) => {
    const key = String(rawKey || '').trim().toLowerCase();
    const canonical = HEADER_LOOKUP[key];
    if (!canonical) return;
    normalized[canonical] = rawRow[rawKey];
  });
  return normalized;
};

const isRowEmpty = (row) => PRODUCT_IMPORT_COLUMNS.every((column) => {
  const value = row[column];
  return value === undefined || value === null || String(value).trim() === '';
});

const emptyToNull = (value) => {
  if (value === undefined || value === null) return null;
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
};

const parseNumber = (value, integerOnly = false) => {
  if (value === undefined || value === null || String(value).trim() === '') return null;
  const numeric = Number(String(value).trim());
  if (!Number.isFinite(numeric)) return Number.NaN;
  if (integerOnly && !Number.isInteger(numeric)) return Number.NaN;
  return numeric;
};

const validateHeaders = (rawRows) => {
  if (!rawRows.length) {
    return REQUIRED_COLUMNS.map((column) => ({
      row: 1,
      field: column,
      error: `Missing required header "${column}"`,
    }));
  }
  const normalizedHeaders = new Set();
  Object.keys(rawRows[0] || {}).forEach((header) => {
    const canonical = HEADER_LOOKUP[String(header || '').trim().toLowerCase()];
    if (canonical) normalizedHeaders.add(canonical);
  });
  return REQUIRED_COLUMNS
    .filter((requiredHeader) => !normalizedHeaders.has(requiredHeader))
    .map((requiredHeader) => ({
      row: 1,
      field: requiredHeader,
      error: `Missing required header "${requiredHeader}"`,
    }));
};

const validateAndNormalizeRows = (rawRows) => {
  const errors = [];
  const validRows = [];
  const seenSkus = new Set();

  rawRows.forEach((rawRow, index) => {
    const rowNumber = index + 2;
    const row = normalizeRawRow(rawRow);
    if (isRowEmpty(row)) return;

    const normalized = {};
    PRODUCT_IMPORT_COLUMNS.forEach((column) => {
      const value = row[column];
      normalized[column] = CURRENCY_COLUMNS.includes(column)
        ? (emptyToNull(value) || 'GBP').toUpperCase()
        : emptyToNull(value);
    });

    REQUIRED_COLUMNS.forEach((column) => {
      if (!normalized[column]) {
        errors.push({
          row: rowNumber,
          field: column,
          error: `${column} is required`,
        });
      }
    });

    NUMBER_COLUMNS.forEach((column) => {
      const parsed = parseNumber(normalized[column], false);
      if (Number.isNaN(parsed)) {
        errors.push({
          row: rowNumber,
          field: column,
          error: `${column} must be a valid number`,
        });
      } else if (parsed !== null) {
        normalized[column] = parsed;
      } else {
        normalized[column] = null;
      }
    });

    INTEGER_COLUMNS.forEach((column) => {
      const parsed = parseNumber(normalized[column], true);
      if (Number.isNaN(parsed)) {
        errors.push({
          row: rowNumber,
          field: column,
          error: `${column} must be a whole number`,
        });
      } else if (parsed !== null) {
        normalized[column] = parsed;
      } else {
        normalized[column] = null;
      }
    });

    if (normalized.item_type && !ALLOWED_ITEM_TYPES.has(normalized.item_type.toLowerCase())) {
      errors.push({
        row: rowNumber,
        field: 'item_type',
        error: `item_type must be one of: ${Array.from(ALLOWED_ITEM_TYPES).join(', ')}`,
      });
    } else if (normalized.item_type) {
      normalized.item_type = normalized.item_type.toLowerCase();
    }

    CURRENCY_COLUMNS.forEach((column) => {
      if (normalized[column] && !ALLOWED_CURRENCIES.has(normalized[column])) {
        errors.push({
          row: rowNumber,
          field: column,
          error: `Unsupported currency "${normalized[column]}"`,
        });
      }
    });

    const skuKey = (normalized.sku_number || '').toLowerCase();
    if (skuKey) {
      if (seenSkus.has(skuKey)) {
        errors.push({
          row: rowNumber,
          field: 'sku_number',
          error: 'Duplicate SKU in import file',
        });
      } else {
        seenSkus.add(skuKey);
      }
    }

    const hasRowError = errors.some((entry) => entry.row === rowNumber);
    if (!hasRowError) {
      validRows.push({
        ...normalized,
        __rowNumber: rowNumber,
      });
    }
  });

  return { validRows, errors };
};

const parseCsv = async (file) => {
  const text = await file.text();
  const { data, errors } = Papa.parse(text, {
    header: true,
    skipEmptyLines: 'greedy',
  });
  const parseErrors = (errors || []).map((entry) => ({
    row: (entry.row || 0) + 1,
    field: 'file',
    error: entry.message || 'Unable to parse CSV row',
  }));
  return { rows: data || [], parseErrors };
};

const parseXlsx = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const names = workbook.SheetNames || [];
  const preferred = names.find((n) => String(n).trim().toLowerCase() === 'products');
  const sheetName = preferred || names[0];
  if (!sheetName) return { rows: [], parseErrors: [] };
  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return { rows, parseErrors: [] };
};

export const parseAndValidateProductImportFile = async (file) => {
  const fileName = String(file?.name || '').toLowerCase();
  let parsed;
  if (fileName.endsWith('.csv')) {
    parsed = await parseCsv(file);
  } else if (fileName.endsWith('.xlsx')) {
    parsed = await parseXlsx(file);
  } else {
    return {
      validRows: [],
      errors: [{ row: 0, field: 'file', error: 'Unsupported file type. Please upload CSV or XLSX.' }],
    };
  }

  const headerErrors = validateHeaders(parsed.rows);
  if (headerErrors.length > 0) {
    return { validRows: [], errors: [...headerErrors, ...parsed.parseErrors] };
  }

  const validated = validateAndNormalizeRows(parsed.rows);
  return {
    validRows: validated.validRows,
    errors: [...parsed.parseErrors, ...validated.errors],
  };
};

const triggerDownload = (fileName, blob) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadProductTemplateCsv = () => {
  const csv = Papa.unparse([EXAMPLE_ROW], { columns: PRODUCT_IMPORT_COLUMNS });
  triggerDownload('products-template.csv', new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
};

export const downloadProductTemplateXlsx = () => {
  const worksheet = XLSX.utils.json_to_sheet([EXAMPLE_ROW], { header: PRODUCT_IMPORT_COLUMNS });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');
  XLSX.writeFile(workbook, 'products-template.xlsx');
};
